"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { VideoSource } from "@/lib/types";

// Attaches a <video>'s real `src` only once the element is both laid out and
// close to the viewport, then starts playback.
//
// Why this exists: the hero renders two variants — a static one for sm/md and
// the GSAP scroll-expand one for lg — and the breakpoint is a CSS `hidden`
// class, so BOTH <video> elements are in the DOM on every viewport. With a
// `src` attribute on each, the browser downloads the file twice regardless of
// which one is visible. Leaving `src` off at render and attaching it from here
// means a `display: none` variant never intersects, so it never downloads.
//
// It also keeps the hero off the critical path: the poster paints as the LCP
// candidate, and the video bytes are requested after `load`.
export const useDeferredVideo = (ref, sources) => {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== ref || $[1] !== sources) {
    t0 = () => {
      const video = ref.current;
      if (!video || sources.length === 0) {
        return;
      }
      let observer = null;
      let cancelled = false;
      const pickSource = () => sources.find(source => video.canPlayType(source.type) !== "")?.src ?? sources[sources.length - 1].src;
      const attach = () => {
        if (cancelled || video.dataset.srcAttached === "true") {
          return;
        }
        video.dataset.srcAttached = "true";
        video.src = pickSource();
        video.load();
        video.play().catch(_temp);
      };
      const observe = () => {
        if (cancelled) {
          return;
        }
        observer = new IntersectionObserver(entries => {
          if (!entries.some(_temp2)) {
            return;
          }
          attach();
          observer?.disconnect();
        }, {
          rootMargin: "200px"
        });
        observer.observe(video);
      };
      if (document.readyState === "complete") {
        observe();
      } else {
        window.addEventListener("load", observe, {
          once: true
        });
      }
      return () => {
        cancelled = true;
        observer?.disconnect();
        window.removeEventListener("load", observe);
      };
    };
    t1 = [ref, sources];
    $[0] = ref;
    $[1] = sources;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};
function _temp() {}
function _temp2(entry) {
  return entry.isIntersecting;
}